import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import type { ExportJob } from '../services/exportService';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DescriptionIcon from '@mui/icons-material/Description';

const STATUS_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pending' },
  processing: { bg: 'bg-primary/10', text: 'text-primary', label: 'Processing' },
  done: { bg: 'bg-success/10', text: 'text-success', label: 'Done' },
  error: { bg: 'bg-danger/10', text: 'text-danger', label: 'Error' },
};

const Export: React.FC = () => {
  const api = useApi();
  const [history, setHistory] = useState<ExportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeJob, setActiveJob] = useState<ExportJob | null>(null);
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'product-report' | 'tabular-form'>('product-report');
  const pollRef = useRef<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Product-report form state
  const [productReportForm, setProductReportForm] = useState({
    product_id: '', title: '', brand: '', upto_date: '',
  });

  // Tabular form state
  const [tabularForm, setTabularForm] = useState({
    category: '', brand: '', date: '', product_id: '', title: '',
    name: '', sku: '', company: '', in_stock_only: false,
  });

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.export.history(20);
      setHistory(res.jobs);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => () => { if (pollRef.current) window.clearInterval(pollRef.current); }, []);

  const pollStatus = useCallback(async (jobId: number) => {
    try {
      const res = await api.export.get(jobId);
      setActiveJob(res.job);
      if (res.job.status === 'done' || res.job.status === 'error') {
        if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
        if (res.job.status === 'done') {
          const dl = await api.export.downloadUrl(jobId);
          setDownloadUrl(dl.url);
          showToast('Export ready — download started', 'success');
        } else {
          showToast(`Export failed: ${res.job.error}`, 'error');
        }
        await loadHistory();
      }
    } catch (err) {
      console.error('poll', err);
    }
  }, [api, loadHistory]);

  useEffect(() => {
    if (activeJobId && pollRef.current === null) {
      pollRef.current = window.setInterval(() => pollStatus(activeJobId), 2000);
    }
    return () => {
      if (!activeJobId && pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [activeJobId, pollStatus]);

  const submitProductReport = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setActiveJob(null);
    setDownloadUrl(null);
    try {
      const body: Record<string, unknown> = {};
      if (productReportForm.product_id) body.product_id = productReportForm.product_id;
      if (productReportForm.title) body.title = productReportForm.title;
      if (productReportForm.brand) body.brand = productReportForm.brand;
      if (productReportForm.upto_date) body.upto_date = productReportForm.upto_date;
      const res = await api.export.productReport(body);
      setActiveJobId(res.jobId);
      await loadHistory();
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const submitTabularForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setActiveJob(null);
    setDownloadUrl(null);
    try {
      const body: Record<string, unknown> = { ...tabularForm };
      if (!tabularForm.in_stock_only) delete body.in_stock_only;
      const res = await api.export.tabularForm(body);
      setActiveJobId(res.jobId);
      await loadHistory();
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this export record?')) return;
    try {
      await api.export.remove(id);
      showToast('Export record deleted', 'success');
      await loadHistory();
    } catch (err) {
      showToast((err as Error).message, 'error');
    }
  };

  const getDownload = async (id: number) => {
    try {
      const dl = await api.export.downloadUrl(id);
      window.open(dl.url, '_blank');
    } catch (err) {
      showToast((err as Error).message, 'error');
    }
  };

  const formatParams = (raw: string): string => {
    try {
      const parsed = JSON.parse(raw);
      const entries = Object.entries(parsed).filter(([_, v]) => v !== '' && v !== null && v !== false);
      if (entries.length === 0) return '(no filters)';
      return entries.map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(', ');
    } catch {
      return raw.slice(0, 60);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-headline-lg font-bold text-on-surface">Export</h1>
          <p className="text-on-surface-variant font-body-sm mt-0.5">Generate XLSX reports from product data</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-7 gap-6">
        {/* Form panel */}
        <div className="lg:col-span-4">
          <div className="bg-surface border border-border-subtle rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-border-subtle">
              <h2 className="font-headline-md text-on-surface font-semibold">Generate Export</h2>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border-subtle">
              <button onClick={() => setActiveTab('product-report')}
                className={`flex items-center gap-2 px-5 py-3 text-body-sm font-semibold border-b-2 transition-colors ${activeTab === 'product-report' ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant hover:text-on-surface'}`}>
                <MoreVertIcon className="text-[16px]" />
                Product Report
              </button>
              <button onClick={() => setActiveTab('tabular-form')}
                className={`flex items-center gap-2 px-5 py-3 text-body-sm font-semibold border-b-2 transition-colors ${activeTab === 'tabular-form' ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant hover:text-on-surface'}`}>
                <DescriptionIcon className="text-[16px]" />
                Tabular Form
              </button>
            </div>

            <div className="p-5">
              {error && (
                <div className="flex items-center gap-2 bg-danger/10 border border-danger/20 text-danger text-body-sm px-3 py-2 rounded-lg mb-4">
                  <MoreVertIcon className="text-[16px]" />
                  {error}
                </div>
              )}

              {activeTab === 'product-report' ? (
                <form onSubmit={submitProductReport} className="space-y-4">
                  <p className="text-on-surface-variant text-body-sm">Generates one XLSX per product with pivoted date x competitor price columns.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">Product IDs (comma-separated)</label>
                      <input type="text" value={productReportForm.product_id}
                        onChange={(e) => setProductReportForm({ ...productReportForm, product_id: e.target.value })}
                        className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2.5 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                        placeholder="1254,569 or leave empty" />
                    </div>
                    <div>
                      <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">Title filter (competitor)</label>
                      <input type="text" value={productReportForm.title}
                        onChange={(e) => setProductReportForm({ ...productReportForm, title: e.target.value })}
                        className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2.5 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                        placeholder="amazon, primeabgb" />
                    </div>
                    <div>
                      <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">Brand filter</label>
                      <input type="text" value={productReportForm.brand}
                        onChange={(e) => setProductReportForm({ ...productReportForm, brand: e.target.value })}
                        className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2.5 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                        placeholder="THERMALTAKE" />
                    </div>
                    <div>
                      <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">From date (YYYY-MM-DD)</label>
                      <input type="date" value={productReportForm.upto_date}
                        onChange={(e) => setProductReportForm({ ...productReportForm, upto_date: e.target.value })}
                        className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2.5 text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                    </div>
                  </div>
                  <button type="submit" disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-on-primary py-2.5 rounded-lg font-body-sm font-semibold hover:bg-primary/90 transition-colors shadow-md disabled:opacity-50">
                    {submitting ? (
                      <><div className="w-4 h-4 border-2 border-on-primary border-t-transparent rounded-full animate-spin" />Generating…</>
                    ) : (
                      <><FactCheckIcon className="text-[18px]" />Generate</>
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={submitTabularForm} className="space-y-4">
                  <p className="text-on-surface-variant text-body-sm">Single XLSX with per-product rows; competitor price + stock columns + MD Computers anchor + hyperlinks.</p>
                  <div className="grid grid-cols-2 gap-4">
                    {([
                      ['category', 'Category', 'text'], ['brand', 'Brand', 'text'],
                      ['date', 'Date (YYYY-MM-DD)', 'date'], ['product_id', 'Product IDs', 'text'],
                      ['title', 'Title', 'text'], ['name', 'Name', 'text'],
                      ['sku', 'SKU', 'text'], ['company', 'Company', 'text'],
                    ] as [string, string, string][]).map(([key, label, type]) => (
                      <div key={key}>
                        <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">{label}</label>
                        <input type={type} value={(tabularForm as Record<string,string | boolean>)[key] as string}
                          onChange={(e) => setTabularForm({ ...tabularForm, [key]: e.target.value })}
                          className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2.5 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                          placeholder={label} />
                      </div>
                    ))}
                    <div className="col-span-2 flex items-center gap-3">
                      <input type="checkbox" id="in_stock_only" checked={tabularForm.in_stock_only}
                        onChange={(e) => setTabularForm({ ...tabularForm, in_stock_only: e.target.checked })}
                        className="w-4 h-4 rounded border-border-subtle text-primary focus:ring-primary/30" />
                      <label htmlFor="in_stock_only" className="text-body-sm text-on-surface cursor-pointer">In stock only</label>
                    </div>
                  </div>
                  <button type="submit" disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-on-primary py-2.5 rounded-lg font-body-sm font-semibold hover:bg-primary/90 transition-colors shadow-md disabled:opacity-50">
                    {submitting ? (
                      <><div className="w-4 h-4 border-2 border-on-primary border-t-transparent rounded-full animate-spin" />Generating…</>
                    ) : (
                      <><FactCheckIcon className="text-[18px]" />Generate</>
                    )}
                  </button>
                </form>
              )}

              {/* Active job status */}
              {activeJob && (
                <div className={`mt-4 p-4 rounded-xl border ${STATUS_STYLE[activeJob.status]?.bg ?? 'bg-surface-high'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-body-sm">Job #{activeJob.id} ({activeJob.type})</span>
                    <span className={`text-[10px] font-label-caps px-2 py-0.5 rounded-full ${STATUS_STYLE[activeJob.status]?.bg ?? 'bg-surface-high'} ${STATUS_STYLE[activeJob.status]?.text ?? 'text-on-surface'}`}>
                      {STATUS_STYLE[activeJob.status]?.label ?? activeJob.status}
                    </span>
                  </div>
                  {activeJob.status === 'processing' && <div className="w-full bg-surface-high rounded-full h-1.5 mt-2 overflow-hidden"><div className="bg-primary h-full rounded-full animate-pulse" style={{ width: '60%' }} /></div>}
                  {activeJob.row_count != null && <p className="text-body-sm mt-1">Rows: {activeJob.row_count}</p>}
                  {activeJob.error && <p className="text-danger text-[11px] mt-1">{activeJob.error}</p>}
                  {activeJob.status === 'done' && downloadUrl && (
                    <a href={downloadUrl} target="_blank" rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-2 bg-success text-white px-3 py-1.5 rounded-lg text-body-sm font-semibold hover:bg-success/90 transition-colors">
                      <MoreVertIcon className="text-[16px]" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* History panel */}
        <div className="lg:col-span-3">
          <div className="bg-surface border border-border-subtle rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-border-subtle">
              <h2 className="font-headline-md text-on-surface font-semibold">Export History</h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-subtle bg-surface-low">
                      <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-3 py-3 sm:px-4">ID</th>
                      <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-3 py-3 sm:px-4">Type</th>
                      <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-3 py-3 sm:px-4">Status</th>
                      <th className="hidden md:table-cell text-left text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-4 py-3">Params</th>
                      <th className="hidden md:table-cell text-left text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-4 py-3">When</th>
                      <th className="text-right text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-3 py-3 sm:px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 ? (
                      <tr><td colSpan={6} className="text-center text-on-surface-variant text-body-sm py-12">No exports yet</td></tr>
                    ) : history.map((j) => {
                      const s = STATUS_STYLE[j.status] ?? { bg: 'bg-surface-high', text: 'text-on-surface-variant', label: j.status };
                      return (
                        <tr key={j.id} className="border-b border-border-subtle last:border-0 hover:bg-surface-low/50 transition-colors">
                          <td className="font-mono text-body-sm px-3 py-3 sm:px-4">{j.id}</td>
                          <td className="px-3 py-3 sm:px-4">
                            <span className="text-[10px] font-label-caps bg-surface-high text-on-surface-variant px-2 py-0.5 rounded-full border border-border-subtle">{j.type}</span>
                          </td>
                          <td className="px-3 py-3 sm:px-4">
                            <span className={`text-[10px] font-label-caps px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>{s.label}</span>
                          </td>
                          <td className="hidden md:table-cell text-on-surface-variant text-[11px] px-4 py-3 max-w-[150px] truncate" title={j.params}>{formatParams(j.params)}</td>
                          <td className="hidden md:table-cell text-on-surface-variant text-body-sm px-4 py-3">{j.created_at}</td>
                          <td className="px-3 py-3 sm:px-4">
                            <div className="flex items-center justify-end gap-2">
                              {j.status === 'done' && (
                                <button onClick={() => getDownload(j.id)}
                                  className="flex items-center gap-1 text-primary text-body-sm hover:text-primary/80 transition-colors">
<MoreVertIcon className="text-[16px]" />
                                </button>
                              )}
                              <button onClick={() => remove(j.id)}
                                className="flex items-center gap-1 text-danger text-body-sm hover:text-danger/80 transition-colors">
<MoreVertIcon className="text-[16px]" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <div className={`fixed bottom-4 right-4 z-[100] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-body-sm font-semibold animate-toast-in ${toast.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'}`}>
          <span className="material-symbols-outlined text-[16px]">{toast.type === 'success' ? 'check' : 'error'}</span>
          {toast.msg}
        </div>
      )}
    </>
  );
};

export default Export;
